import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { setAuthErrorHandler, authAPI } from '../services/api'
import { clearTtsProfileClientCache } from '../services/ttsProfileClient'
import { resolveRole, interpretValidation, buildSsoSession, isSessionCurrent } from './authSession'
import { clearContinuation } from './oauthContinuation'

const AuthContext = createContext(null)

// Token validation - our tokens are random hex strings, not JWTs
// Tokens are validated server-side; we just check if token exists
function hasValidToken(token) {
  return !!token && typeof token === 'string' && token.length > 0
}

function getDetectedTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    return ''
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [emailVerified, setEmailVerified] = useState(true)

  // Handle auth errors (called from the API interceptor). `requestToken` is the token the FAILING request was issued against;
  // if a newer session has since replaced it (e.g. an SSO login completed while an old request was in flight), this stale 401
  // must NOT clear the current session.
  const handleAuthError = useCallback((requestToken) => {
    if (!isSessionCurrent(requestToken, localStorage.getItem('rithmo_token'))) return
    localStorage.removeItem('rithmo_user')
    localStorage.removeItem('rithmo_token')
    clearTtsProfileClientCache()
    setUser(null)
    setIsAuthenticated(false)
    setEmailVerified(true)
  }, [])

  useEffect(() => {
    // Server-validated restore on mount (plan §Stage 3, T01). The SPA trusts the SERVER, not localStorage: a session can be
    // revoked or an SSO session can hit its finite lifetime (O1) while a stale rithmo_user sits in storage. We call
    // /api/auth/validate and act on interpretValidation():
    //   adopt-server → authoritative user + active org;  sign-out → 401 (session gone);  keep-local → 404 (SSO flag off) / offline.
    // The keep-local path preserves the exact prior behavior when the feature flag is off, so there is no regression.
    let cancelled = false
    const restore = async () => {
      const token = localStorage.getItem('rithmo_token')
      if (!hasValidToken(token)) {
        setLoading(false)
        return
      }
      const startToken = token // the session this restore is validating
      let localUser = null
      const savedUser = localStorage.getItem('rithmo_user')
      if (savedUser) {
        try {
          localUser = JSON.parse(savedUser)
        } catch {
          localStorage.removeItem('rithmo_user')
        }
      }

      let decision
      try {
        const res = await authAPI.validateSession()
        const serverResult = res?.data ?? res ?? null
        decision = interpretValidation({ localUser, serverResult })
      } catch (err) {
        decision = interpretValidation({ localUser, errorStatus: err?.status ?? null })
      }
      if (cancelled) return
      // A newer session (e.g. an SSO completion) may have replaced the token while /validate was in flight. Discard this stale
      // result — neither a late success nor a late 401 may touch the current session.
      if (!isSessionCurrent(startToken, localStorage.getItem('rithmo_token'))) {
        setLoading(false)
        return
      }

      if (decision.action === 'sign-out') {
        localStorage.removeItem('rithmo_user')
        localStorage.removeItem('rithmo_token')
        clearTtsProfileClientCache()
        setUser(null)
        setIsAuthenticated(false)
        setEmailVerified(true)
      } else {
        setUser(decision.user)
        setIsAuthenticated(true)
        setEmailVerified(decision.user?.emailVerified !== false)
        localStorage.setItem('rithmo_user', JSON.stringify(decision.user))
      }
      setLoading(false)
    }
    restore()
    return () => {
      cancelled = true
    }
  }, [])

  // Register auth error handler with API service
  useEffect(() => {
    setAuthErrorHandler(handleAuthError)
    return () => setAuthErrorHandler(null)
  }, [handleAuthError])

  const login = async (email, password) => {
    if (!email || !password) {
      throw new Error('Email and password are required')
    }
    try {
      const detectedTz = getDetectedTimezone()
      const res = await authAPI.login(email, password, detectedTz || null)
      const payload = res?.data ?? res
      const token = payload?.token ?? res?.token
      const userData = payload?.user ?? res?.user
      const verified = payload?.email_verified ?? res?.email_verified ?? true
      const setupProfile = payload?.setupProfile ?? res?.setupProfile
      const nextSetupPath = payload?.nextSetupPath ?? res?.nextSetupPath
      if (token) {
        localStorage.setItem('rithmo_token', token)
      }
      const userBase = userData
        ? { ...userData, emailVerified: verified, role: resolveRole(userData) }
        : {
            id: 'user_' + Date.now(),
            email,
            name: email.split('@')[0],
            avatar: null,
            emailVerified: verified,
            role: 'member', // server-authoritative; never promote by name (see authSession.resolveRole)
            createdAt: new Date().toISOString(),
            settings: { notifications: true, theme: 'dark', defaultMeetingDuration: 30 },
            integrations: { google: false, outlook: false, zoom: false },
          }
      const user = setupProfile !== undefined ? { ...userBase, setupProfile } : userBase
      const userWithPaths =
        typeof nextSetupPath === 'string' && nextSetupPath.trim()
          ? { ...user, nextSetupPath }
          : user
      localStorage.setItem('rithmo_user', JSON.stringify(userWithPaths))
      setUser(userWithPaths)
      setIsAuthenticated(true)
      setEmailVerified(verified)
      clearTtsProfileClientCache()
      return userWithPaths
    } catch (err) {
      throw new Error(err.message || 'Invalid email or password')
    }
  }

  const signup = async (email, password, name, metadata = {}) => {
    const company_name = typeof metadata.company_name === 'string' ? metadata.company_name.trim() : ''
    const job_title = typeof metadata.job_title === 'string' ? metadata.job_title.trim() : ''
    const signup_role_band = metadata.signup_role_band
    // Joining through a company join link: the company already exists, so no company questions are asked or sent.
    const join_link = typeof metadata.join_link === 'string' ? metadata.join_link.trim() : ''
    if (!email || !password || !(name ?? '').trim() || (!join_link && (!company_name || !job_title || !signup_role_band))) {
      throw new Error('Please fill in all fields')
    }
    try {
      const timezone = getDetectedTimezone()
      const body = {
        full_name: name,
        email,
        password,
        timezone: timezone || undefined,
        ...(join_link ? { join_link } : { company_name, job_title, signup_role_band }),
      }
      const res = await authAPI.signup(body)
      const payload = res?.data ?? res
      const token = payload?.token ?? res?.token
      const userData = payload?.user ?? res?.user
      const verified = payload?.email_verified ?? res?.email_verified ?? true
      const setupProfile = payload?.setupProfile ?? res?.setupProfile
      const nextSetupPath = payload?.nextSetupPath ?? res?.nextSetupPath
      if (token) {
        localStorage.setItem('rithmo_token', token)
      }
      const userBase = userData
        ? { ...userData, emailVerified: verified, role: resolveRole(userData) }
        : {
            id: 'user_' + Date.now(),
            email,
            name,
            avatar: null,
            emailVerified: verified,
            role: 'member', // server-authoritative; never promote by name (see authSession.resolveRole)
            createdAt: new Date().toISOString(),
            settings: { notifications: true, theme: 'dark', defaultMeetingDuration: 30 },
            integrations: { google: false, outlook: false, zoom: false },
          }
      const withProfile = setupProfile !== undefined ? { ...userBase, setupProfile } : userBase
      const userStored =
        typeof nextSetupPath === 'string' && nextSetupPath.trim()
          ? { ...withProfile, nextSetupPath: nextSetupPath.trim() }
          : withProfile
      localStorage.setItem('rithmo_user', JSON.stringify(userStored))
      setUser(userStored)
      setIsAuthenticated(true)
      setEmailVerified(verified)
      clearTtsProfileClientCache()
      return userStored
    } catch (err) {
      throw new Error(err.message || 'Please fill in all fields')
    }
  }

  // Shared-signin SSO (plan §Stage 3): redeem the completion code delivered on the ?sso_code= callback redirect for a Rithmo
  // bearer. The bearer arrives in the response BODY only (never a cookie/URL) and is stored exactly like a password login.
  // Returns the post-login destination (return_to) so the caller can navigate. No profile form is ever shown.
  const completeSsoLogin = async (code) => {
    const res = await authAPI.ssoComplete(code)
    const payload = res?.data ?? res
    const { token, user: nextUser, returnTo } = buildSsoSession(payload)
    localStorage.setItem('rithmo_token', token)
    localStorage.setItem('rithmo_user', JSON.stringify(nextUser))
    setUser(nextUser)
    setIsAuthenticated(true)
    setEmailVerified(true)
    clearTtsProfileClientCache()
    return { user: nextUser, returnTo }
  }

  const logout = async () => {
    try {
      const token = localStorage.getItem('rithmo_token')
      if (token) {
        await fetch(`${import.meta.env.VITE_API_URL || '/api'}/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => {})
      }
    } finally {
      localStorage.removeItem('rithmo_user')
      localStorage.removeItem('rithmo_token')
      clearContinuation() // R3-5: a stale MCP OAuth continuation must not survive logout → a later ordinary login would resume it
      clearTtsProfileClientCache()
      setUser(null)
      setIsAuthenticated(false)
      setEmailVerified(true)
    }
  }

  const updateUser = (updates) => {
    const normalizedUpdates = { ...updates }
    if (normalizedUpdates.role === 'user') {
      normalizedUpdates.role = 'member'
    }
    const updatedUser = {
      ...user,
      ...normalizedUpdates,
      role: normalizedUpdates.role || resolveRole({ ...user, ...normalizedUpdates }),
    }
    localStorage.setItem('rithmo_user', JSON.stringify(updatedUser))
    setUser(updatedUser)
  }

  const verifyEmail = async (otp) => {
    const res = await authAPI.verifyEmail(otp)
    if (res?.data?.verified || res?.verified) {
      setEmailVerified(true)
      const updated = { ...user, emailVerified: true }
      setUser(updated)
      localStorage.setItem('rithmo_user', JSON.stringify(updated))
    }
    return res
  }

  const resendOtp = async () => {
    return authAPI.resendOtp()
  }

  const value = {
    user,
    loading,
    isAuthenticated,
    emailVerified,
    login,
    signup,
    completeSsoLogin,
    logout,
    updateUser,
    verifyEmail,
    resendOtp,
    handleAuthError,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext
